import React, { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pencil } from 'lucide-react';
import { formatDate } from '@/utils/date';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

export function RegisterRequests() {
  const { registerRequests = [], approveRegisterRequest, rejectRegisterRequest, updateRegisterRequest } = useAuthStore();
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [editingRequest, setEditingRequest] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<'user' | 'admin'>('user');
  const [editedData, setEditedData] = useState<any>(null);

  // Filter pending requests first
  const pendingRequests = registerRequests.filter(r => r.status === 'pending');

  const handleApprove = () => {
    if (selectedRequest) {
      approveRegisterRequest(selectedRequest.id, selectedRole);
      setSelectedRequest(null);
    }
  };

  const handleEdit = (request: any) => {
    setEditingRequest(request);
    setEditedData({ ...request.data });
  };

  const handleSaveEdit = () => {
    if (editingRequest && editedData) {
      updateRegisterRequest(editingRequest.id, editedData);
      setEditingRequest(null);
      setEditedData(null);
    }
  };

  const handleReject = () => {
    if (selectedRequest) {
      rejectRegisterRequest(selectedRequest.id);
      setSelectedRequest(null);
    }
  };

  const formatBirthDate = (birthDate: any) => {
    if (!birthDate) return 'غير محدد';
    return formatDate(new Date(birthDate));
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">طلبات التسجيل</h2>

      <div className="space-y-4">
        {pendingRequests.map((request) => (
          <div key={request.id} className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-start">
              <div className="space-y-4 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{request.data.name}</h3>
                  <Badge variant="secondary">{request.data.occupation}</Badge>
                  <Badge variant="warning">قيد المراجعة</Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  {/* Personal Information */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-primary">المعلومات الشخصية</h4>
                    <p>
                      <span className="font-medium ml-1">رقم الجوال:</span>
                      <span dir="ltr">{request.data.phoneNumber}</span>
                    </p>
                    <p>
                      <span className="font-medium ml-1">تاريخ الميلاد:</span>
                      {formatBirthDate(request.data.birthDate)}
                    </p>
                    <p>
                      <span className="font-medium ml-1">الحالة الاجتماعية:</span>
                      {request.data.maritalStatus}
                    </p>
                  </div>

                  {/* Family Information */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-primary">معلومات العائلة</h4>
                    <p>
                      <span className="font-medium ml-1">اسم الأب:</span>
                      {request.data.fatherName}
                    </p>
                    <p>
                      <span className="font-medium ml-1">اسم الجد:</span>
                      {request.data.grandfather1}
                    </p>
                    <p>
                      <span className="font-medium ml-1">اسم الجد الثاني:</span>
                      {request.data.grandfather2}
                    </p>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-primary">معلومات إضافية</h4>
                    <p>
                      <span className="font-medium ml-1">المنطقة:</span>
                      {request.data.region}
                    </p>
                    <p>
                      <span className="font-medium ml-1">المهنة:</span>
                      {request.data.occupation}
                    </p>
                    <p>
                      <span className="font-medium ml-1">المرحلة الدراسية:</span>
                      {request.data.educationLevel}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleEdit(request)}
                    className="hover:bg-secondary"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => setSelectedRequest(request)}
                    className="hover:bg-secondary"
                    variant="outline"
                  >
                    قبول / رفض
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Approve/Reject Dialog */}
      {selectedRequest && (
        <Dialog open={true} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>تأكيد الطلب</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>اختر الدور</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant={selectedRole === 'user' ? 'default' : 'outline'}
                    onClick={() => setSelectedRole('user')}
                  >
                    مستخدم
                  </Button>
                  <Button
                    variant={selectedRole === 'admin' ? 'default' : 'outline'}
                    onClick={() => setSelectedRole('admin')}
                  >
                    مسؤول
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleApprove} className="mr-2">
                قبول
              </Button>
              <Button variant="destructive" onClick={handleReject}>
                رفض
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Edit Dialog */}
      {editingRequest && (
        <Dialog open={true} onOpenChange={() => setEditingRequest(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>تعديل بيانات الطلب</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {/* Personal Information */}
              <div className="space-y-2">
                <Label>الاسم</Label>
                <Input
                  value={editedData.name}
                  onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الجوال</Label>
                <Input
                  value={editedData.phoneNumber}
                  onChange={(e) => setEditedData({ ...editedData, phoneNumber: e.target.value })}
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label>المنطقة</Label>
                <Input
                  value={editedData.region}
                  onChange={(e) => setEditedData({ ...editedData, region: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>المهنة</Label>
                <Input
                  value={editedData.occupation}
                  onChange={(e) => setEditedData({ ...editedData, occupation: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>اسم الأب</Label>
                <Input
                  value={editedData.fatherName}
                  onChange={(e) => setEditedData({ ...editedData, fatherName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>اسم الجد</Label>
                <Input
                  value={editedData.grandfather1}
                  onChange={(e) => setEditedData({ ...editedData, grandfather1: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>اسم الجد الثاني</Label>
                <Input
                  value={editedData.grandfather2}
                  onChange={(e) => setEditedData({ ...editedData, grandfather2: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>المرحلة الدراسية</Label>
                <Input
                  value={editedData.educationLevel}
                  onChange={(e) => setEditedData({ ...editedData, educationLevel: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter className="sm:justify-start">
              <Button onClick={handleSaveEdit}>
                حفظ التعديلات
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setEditingRequest(null);
                  setEditedData(null);
                }}
                className="mr-2"
              >
                إلغاء
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}